import { useEffect } from "react";

interface BrandData {
  name: string;
  logo: string;
  website: string;
  category: string;
  description?: string;
}

interface StructuredDataProps {
  type: "organization" | "product" | "brand" | "article" | "breadcrumb" | "local-business" | "faq" | "website";
  data?: any;
  breadcrumbs?: Array<{ name: string; url: string }>;
  products?: Array<{ name: string; description: string; image: string; category: string }>;
  brand?: BrandData;
  article?: {
    headline: string;
    author: string;
    datePublished: string;
    dateModified?: string;
    image: string;
  };
  faq?: Array<{ question: string; answer: string }>;
}

export const StructuredData = ({ type, data, breadcrumbs, products, brand, article, faq }: StructuredDataProps) => {
  useEffect(() => {
    const generateStructuredData = () => {
      const baseUrl = window.location.origin;
      
      switch (type) {
        case "organization":
          return {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Fabbriche Toscane",
            "description": "Eccellenze Artigianali Alimentari e del Benessere: tradizione e innovazione toscana per HORECA e private label.",
            "url": baseUrl,
            "logo": `${baseUrl}/lovable-uploads/70986b36-3825-46b0-a895-ac3b2587014e.png`,
            "foundingDate": "2020",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Via delle Bartoline 41",
              "addressLocality": "Calenzano",
              "addressRegion": "Toscana",
              "postalCode": "50041",
              "addressCountry": "IT"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+39 055 8878480",
              "contactType": "customer service",
              "availableLanguage": ["Italian", "English"]
            },
            "sameAs": [
              "https://www.linkedin.com/company/fabbriche-toscane",
              "https://www.instagram.com/fabbrichetoscane"
            ],
            "makesOffer": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Product",
                  "name": "Prodotti Alimentari Artigianali",
                  "category": "Food & Beverage"
                }
              },
              {
                "@type": "Offer", 
                "itemOffered": {
                  "@type": "Service",
                  "name": "Private Label Solutions",
                  "category": "Business Services"
                }
              }
            ]
          };

        case "product":
          return products?.map(product => ({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": product.name,
            "description": product.description,
            "image": product.image,
            "category": product.category,
            "manufacturer": {
              "@type": "Organization",
              "name": "Fabbriche Toscane"
            },
            "brand": {
              "@type": "Brand",
              "name": "Fabbriche Toscane"
            }
          }));

        case "brand":
          return brand ? {
            "@context": "https://schema.org",
            "@type": "Brand",
            "name": brand.name,
            "logo": `${baseUrl}${brand.logo}`,
            "url": brand.website.startsWith('http') ? brand.website : `${baseUrl}${brand.website}`,
            "manufacturer": {
              "@type": "Organization",
              "name": "Fabbriche Toscane"
            }
          } : null;

        case "breadcrumb":
          return breadcrumbs ? {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbs.map((crumb, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": crumb.name,
              "item": crumb.url.startsWith('http') ? crumb.url : `${baseUrl}${crumb.url}`
            }))
          } : null;

        case "local-business":
          return {
            "@context": "https://schema.org",
            "@type": ["Organization", "LocalBusiness", "Manufacturer"],
            "name": "Fabbriche Toscane",
            "description": "Eccellenze Artigianali Alimentari e del Benessere",
            "url": baseUrl,
            "logo": `${baseUrl}/lovable-uploads/70986b36-3825-46b0-a895-ac3b2587014e.png`,
            "image": `${baseUrl}/lovable-uploads/gruppo-hero.jpg`,
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Via delle Bartoline 41",
              "addressLocality": "Calenzano",
              "addressRegion": "Toscana",
              "postalCode": "50041",
              "addressCountry": "IT"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 43.8631,
              "longitude": 11.1669
            },
            "telephone": "+39 055 8878480",
            "email": "contatti@fabbrichetoscane.it",
            "openingHours": ["Mo-Fr 09:00-13:00", "Mo-Fr 14:00-18:00"],
            "priceRange": "€€€",
            "servesCuisine": ["Italian"],
            "makesOffer": {
              "@type": "Offer",
              "name": "Prodotti Alimentari Artigianali Premium"
            }
          };

        case "article":
          return article ? {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.headline,
            "author": {
              "@type": "Organization",
              "name": article.author || "Fabbriche Toscane"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Fabbriche Toscane",
              "logo": {
                "@type": "ImageObject",
                "url": `${baseUrl}/lovable-uploads/70986b36-3825-46b0-a895-ac3b2587014e.png`
              }
            },
            "datePublished": article.datePublished,
            "dateModified": article.dateModified || article.datePublished,
            "image": article.image,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": window.location.href
            }
          } : null;

        case "faq":
          return faq ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faq.map(item => ({
              "@type": "Question",
              "name": item.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": item.answer
              }
            }))
          } : null;

        case "website":
          return {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Fabbriche Toscane",
            "alternateName": "Fabbriche Toscane - Eccellenze Artigianali",
            "url": baseUrl,
            "potentialAction": {
              "@type": "SearchAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": `${baseUrl}/notizie/blog?q={search_term_string}`
              },
              "query-input": "required name=search_term_string"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Fabbriche Toscane",
              "logo": {
                "@type": "ImageObject",
                "url": `${baseUrl}/lovable-uploads/70986b36-3825-46b0-a895-ac3b2587014e.png`
              }
            }
          };

        default:
          return null;
      }
    };

    const structuredData = generateStructuredData();
    
    if (structuredData) {
      // Create unique ID for this structured data
      const scriptId = `structured-data-${type}`;
      
      // Remove existing script if it exists
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }

      // Add new structured data
      const script = document.createElement("script");
      script.id = scriptId;
      script.setAttribute("type", "application/ld+json");
      script.textContent = JSON.stringify(Array.isArray(structuredData) ? structuredData : [structuredData]);
      document.head.appendChild(script);

      // Cleanup function
      return () => {
        const scriptToRemove = document.getElementById(scriptId);
        if (scriptToRemove) {
          scriptToRemove.remove();
        }
      };
    }
  }, [type, data, breadcrumbs, products, brand, article, faq]);

  return null;
};

export default StructuredData;